import type { ChatTurnInput, ScreenContext } from "~/services/aiOrchestrator";
import type { GridColumnSchema, GridFilterValue } from "~/store/fernieActionSlice";

import wsId from "~/helpers/wsId";

export interface FileInput {
	content: string;
	name: string;
}

interface FileMessageDisplay {
	displayText: string;
	fileName: string;
}

const countRows = (content: string) => content.split("\n").length;

const buildFileHeader = (file: FileInput): string =>
	`[File uploaded: ${file.name} (${countRows(file.content)} rows)]\n\nFile contents:\n${file.content}`;

const buildDefaultInstruction = (fileName: string): string =>
	`I've uploaded ${fileName}. Please analyze it and tell me what data is in it, then ask me what I'd like to do with it.`;

const buildDisplayText = (file: FileInput, userText: string) =>
	userText || `Uploaded ${file.name} - analyze and suggest what to do`;

const buildFileMessageDisplay = (file: FileInput, userText: string): FileMessageDisplay => ({
	displayText: buildDisplayText(file, userText),
	fileName: file.name
});

const formatFilterValue = ({ label, value }: GridFilterValue): string => `${label}=${value}`;

const formatColumnDetail = (col: GridColumnSchema): string => {
	const detail = col.filterValues ? `set: ${col.filterValues.map(formatFilterValue).join(" | ")}` : col.filterType;
	return col.hidden ? [detail, "hidden"].join(", ") : detail;
};

const formatColumn = (col: GridColumnSchema): string =>
	`${col.headerName}${col.param ? ` [param: ${col.param}]` : ""} (${formatColumnDetail(col)})`;

const formatColumnList = (schema: GridColumnSchema[]): string => schema.map(formatColumn).join(", ");

const GRID_TOOL_GUIDANCE = [
	"When you talk to the user, refer to columns and values only by the headers and labels shown here, exactly as they appear on screen. Never mention the bracketed params or the =CODE values in a reply.",
	"To filter a list, use the navigate-to tool with kind ApplyGridFilter. To go to another page, use it with kind NavigateTo. Use a bracketed param and the =CODE values only inside the deepLink query string of navigate-to, for example ?paymentStatus=PARTIALLY_PAID."
].join("\n");

const labelled = (label: string, value: string): string => [label, value].join(": ");

const buildContextString = (gridDataKey: string, schema: GridColumnSchema[]): string =>
	[
		`[${labelled("Active Grid", gridDataKey)}]`,
		labelled("On-screen columns", formatColumnList(schema)),
		GRID_TOOL_GUIDANCE
	].join("\n");

const buildGridContext = (activeGridDataKey: string | null, activeGridSchema: GridColumnSchema[] | null): string =>
	activeGridDataKey === null || activeGridSchema === null || activeGridSchema.length === 0
		? ""
		: buildContextString(activeGridDataKey, activeGridSchema);

const joinSections = (sections: string[]): string | null => (sections.length ? sections.join("\n\n") : null);

const buildTurnInput = (
	typedText: string,
	file: FileInput | null,
	gridContext: string,
	entityContext: string
): ChatTurnInput => {
	const message = typedText.trim();
	const sections: string[] = [];
	if (file) sections.push(buildFileHeader(file));
	if (file && !message) sections.push(buildDefaultInstruction(file.name));
	if (gridContext) sections.push(gridContext);
	if (entityContext) sections.push(entityContext);

	return { attachedContext: joinSections(sections), clientMessageId: wsId(), message };
};

const toRequestInput = (
	{ attachedContext, clientMessageId, message }: ChatTurnInput,
	sessionId: string | null | undefined,
	screenContext: ScreenContext | undefined
) => ({ attachedContext, clientMessageId, message, screenContext, sessionId });

const chatTurn = {
	buildFileMessageDisplay,
	buildGridContext,
	buildTurnInput,
	toRequestInput
};

export default chatTurn;
